import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { ISortBy, IEwtSummary, ITargetItem, IVoucherScenarioItem } from '@nkr/models/index';
import { ListType } from './list-type';
import { Glyphicon, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { GridReadyEvent } from 'ag-grid-community';
import PatientGrid from './patient-grid';

interface IProps {
    list: ITargetItem[],
    checkedTargets: number[],
    checkedChainEndTargets: number[],
    suppress: boolean,
    title: string,
    type: number,
    className: string,
    fullSize: boolean,
    waitTime?: number,
    voucherScenarioMap: IVoucherScenarioItem[],
    onUpdateSuppress: (type: ListType, suppress: boolean) => void,
    onShowEwt: (summary: IEwtSummary, initialCount: any) => void,
    onShowFwt: (recipient: ITargetItem) => void,
    onTargetClicked: (recipientId: number) => void,
    onTriggerRexm: (donorId: number, donorAlias: string, donorCenter: string, recipientId: number, recipientAlias: string, recipientCenter: string) => void,
    onClear: () => void,
    onFullSizeToggle: () => void,
    onGridReady: (e: GridReadyEvent) => void,
    onShowMortalityBreakdown: (type: number, recipient: ITargetItem) => void
}

const List = (props: IProps) => {
    const [sortBy, setSortBy] = useState<ISortBy[]>([]);

    useEffect(() => {
        if (props.type == ListType.PairedPatients) {
            setSortBy([{ property: 'recipientCenter', direction: 'asc' }]);
        }
    }, [])

    const handleUpdateSuppress = (suppress: boolean) => {
        props.onUpdateSuppress(props.type, suppress);
    }

    const handleShowEwt = (summary: IEwtSummary, initialCount: any) => {
        props.onShowEwt(summary, initialCount);
    }

    const handleShowFwt = (recipient: ITargetItem) => {
        props.onShowFwt(recipient);
    }

    const getHardToMatch = () => {
        let rows = props.list;
        let sortByProperties = sortBy.map((sortBy) => sortBy.property);
        let sortByDirections = sortBy.map((sortBy) => sortBy.direction);

        let presorted = _.orderBy(rows, [
            item => item.isMatchable ? 0 : 1,
            item => {
                let donorsInMatch = item.potentialDonors.filter(x => x.donorInMatch);
                return item.potentialDonors.length > 0 && donorsInMatch.length == item.potentialDonors.length ? 0 : 1;
            },
            item => item.sortOrder,
            item => item.recipientAlias
        ]);

        let sortedRows = sortBy.length > 0 ? _.orderBy(rows, sortByProperties, sortByDirections) : presorted;

        let filteredRows = props.suppress ? sortedRows.filter(x => x.potentialDonors.filter(d => d.donorInMatch).length != x.potentialDonors.length || x.isMatchable == true) : sortedRows;

        return filteredRows;
    };

    const getVouchers = () => {
        let rows = props.list;
        let sortByProperties = sortBy.map((sortBy) => sortBy.property);
        let sortByDirections = sortBy.map((sortBy) => sortBy.direction);

        let presorted = _.orderBy(props.list, [
            item => {
                return item.isMatchable ? 0 : 1;
            },
            item => {
                return item.sortOrder;
            },
            item => {
                return item.recipientAlias;
            }
        ]);

        let sortedRows = sortBy.length > 0 ? _.orderBy(rows, sortByProperties, sortByDirections) : presorted;

        let filteredRows = props.suppress ? sortedRows.filter(x => x.isMatchable == true) : sortedRows;

        return filteredRows;
    };

    const getPairPatients = () => {
        let list = props.list.filter(item => {
            let suppress = props.suppress ? item.isMatchable == true : true;
            return suppress;
        });

        let sortByProperties = sortBy.map((sortBy) => sortBy.property);
        let sortByDirections = sortBy.map((sortBy) => sortBy.direction);

        let sortedRows = sortBy.length > 0 ? _.orderBy(list, sortByProperties, sortByDirections) : list;

        return sortedRows;
    };

    const renderHeader = () => {
        return (
            <div style={{ padding: '2px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
                <div style={{ fontSize: 16, width: 200 }}>{props.title}</div>

                {props.type == ListType.HardToMatch && (
                    <div style={{ fontSize: 13 }}>
                        <span style={{ backgroundColor: '#5DFC0A', padding: '2px 5px', borderRadius: 4, border: '1px solid #ddd' }}>Donor Available</span>&nbsp;
                        <span style={{ backgroundColor: 'yellow', padding: '2px 5px', borderRadius: 4, border: '1px solid #ddd' }}>Donor in a Match</span>&nbsp;
                        <span style={{ backgroundColor: 'white', padding: '2px 5px', borderRadius: 4, border: '1px solid #ddd' }}>Unmatchable</span>
                    </div>
                )}

                {props.type == ListType.Vouchers && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: 12, fontWeight: 400 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ height: 20, width: 20, marginRight: 5, borderRadius: 4, backgroundColor: '#FF9999' }}></div>
                            <div>- Compatible</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ height: 20, width: 20, backgroundColor: '#FFC000', marginRight: 5, borderRadius: 4 }}></div>
                            <div>- Special</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ height: 20, width: 20, backgroundColor: '#94DCF8', marginRight: 5, borderRadius: 4 }}></div>
                            <div>- Regular</div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', alignSelf: 'left', alignItems: 'center' }}>
                    <div style={{ paddingRight: 5, color: 'darkgray' }} title={props.fullSize ? 'Click here to show lists with fixed header...' : 'Click here to show lists without fixed headers...'}><Glyphicon glyph={props.fullSize ? 'resize-full' : 'resize-small'} onClick={props.onFullSizeToggle} /></div>
                    <ToggleButtonGroup type='radio' name='suppress' value={props.suppress ? 1 : 0}>
                        <ToggleButton type='radio' value={1} className={props.suppress ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => handleUpdateSuppress(true)}>Suppress</ToggleButton>
                        <ToggleButton type='radio' value={0} className={!props.suppress ? 'btn-primary btn-xs' : 'btn-xs'} onClick={() => handleUpdateSuppress(false)}>Unsuppress</ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </div>
        );
    }

    const list = props.type == ListType.HardToMatch ?
        getHardToMatch()
        :
        props.type == ListType.Vouchers ?
            getVouchers()
            :
            getPairPatients();

    if (list.length == 0) {
        return (
            <>
                <div style={{ marginBottom: 10 }}>
                    {renderHeader()}

                    <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4 }}>
                        No {props.title} found...
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className={props.className} style={{ marginBottom: 10 }}>
            {renderHeader()}

            <PatientGrid
                list={list}
                type={props.type}
                fullSize={props.fullSize}
                showTargetColumn={true}
                showEwtModel={true}
                showFwtModel={true}
                suppressed={props.suppress}
                useRowClass={true}
                isHighDeclines={false}
                onShowEwt={handleShowEwt}
                onShowFwt={handleShowFwt}
                onGridReady={props.onGridReady}
                onShowMortalityBreakdown={props.onShowMortalityBreakdown}
            />
        </div>
    );
}

export default List;